import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';
import { MessageDialogComponent } from 'app/shared/message-dialog/message-dialog.component';
import { UnsavedWorkService } from './unsaved-work.service';

/**
 * Builds links to the external myTrove brochure ("brochureware") site, which now
 * hosts the marketing/content pages that used to live inside this app. The base
 * URL is environment-specific (see `brochureURL` in the environment files); the
 * paths below are the pages as they exist on the new brochure site.
 */
@Injectable({ providedIn: 'root' })
export class BrochureService {

  private readonly base = environment.brochureURL;

  constructor(
    private dialog: MatDialog,
    private unsavedWork: UnsavedWorkService,
  ) {}

  get home(): string { return this.url('/'); }
  get privacy(): string { return this.url('/privacy-policy.html'); }
  get terms(): string { return this.url('/terms-of-use.html'); }
  get contact(): string { return this.url('/contact-us.html'); }
  get disclosure(): string { return this.url('/disclosure.html'); }
  get resources(): string { return this.url('/resources.html'); }

  /** Send the browser to the brochure home page (used for "leave"/root redirects). */
  goHome(): void {
    window.location.href = this.home;
  }

  /**
   * Handles a click on a link that leaves the app for the brochure site. If the user
   * is part-way through a flow with unsaved work, they are asked to confirm first;
   * otherwise the navigation proceeds immediately. Keep the anchor's `href` so that
   * open-in-new-tab still works (that keeps the current tab's progress intact).
   */
  leave(event: Event, url: string = this.home): void {
    // Let modifier-clicks open in a new tab as usual - the current tab (and its
    // progress) stays put, so there is nothing to warn about.
    const mouseEvent = event as MouseEvent;
    if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.shiftKey) {
      return;
    }
    event.preventDefault();
    if (!this.unsavedWork.isFlowInProgress) {
      window.location.href = url;
      return;
    }
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '360px',
      data: {
        title: 'Leave myTrove?',
        message: 'You have unsaved progress that will be lost if you leave now.',
        button1: 'Leave',
        button2: 'Stay',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Leave') {
        this.unsavedWork.permitNavigation();
        window.location.href = url;
      }
    });
  }

  private url(path: string): string {
    // Avoid a double slash when joining the base and an absolute path.
    return `${this.base}${path}`;
  }
}
